<template>
  <div class="about">
    <Hero :title="data?.hero?.title" :description="data?.hero?.description">
      <template #image>
        <ElementsImage v-if="data?.hero?.image" v-bind="data.hero.image" />
      </template>
    </Hero>

    <ElementsContainer>
      <template v-for="(item, index) in data?.content" :key="index">
        <template v-if="item.__component === 'shared.info'">
          <div v-if="!item.image" class="about-heading">
            <div class="about-heading__title">{{ item.title }}</div>
            <div class="about-heading__description">{{ item.description }}</div>
          </div>
          <div v-else class="about-info">
            <div class="about-info__media" :class="[`about-info__media--${item.image_position ?? 'left'}`]">
              <ElementsImage v-bind="item.image" />
            </div>
            <div class="about-info__content">
              <div class="about-info__content-title">{{ item.title }}</div>
              <div class="about-info__content-description">{{ item.description }}</div>
              <NuxtLinkLocale v-if="item.button" :to="item.button.href" :target="item.button.target" class="about-info__content-link">
                {{ item.button.title }}
              </NuxtLinkLocale>
            </div>
          </div>
        </template>
        <template v-else-if="item.__component === 'shared.video'">
          <div class="about-video">
            <video :src="strapiUrl + item.video?.url" muted autoplay loop preload="auto" />
          </div>
        </template>
      </template>
    </ElementsContainer>

    <Social />

    <ElementsContainer>
      <Comments />
    </ElementsContainer>
  </div>
</template>

<script setup lang="ts">
  import fetchApi from "~/utils/fetchApi"

  const config = useRuntimeConfig()
  const strapiUrl = config.public.strapiUrl

  const { data: responseData } = await useAsyncData("about", () =>
    fetchApi<any>("about-page", {
      wrappedByKey: "data",
      populate: {
        hero: { populate: "*" },
        content: { populate: "*" }
      }
    })
  )

  const data = computed(() => responseData.value || {})

  const { t } = useI18n()

  useSeoMeta({
    title: () => data.value?.hero?.title || t("menu.about"),
    description: () => data.value?.hero?.description || ""
  })
</script>

<style lang="scss" scoped>
  .about {
    &-heading {
      @apply flex flex-col gap-7 text-center mx-auto;
      max-width: 848px;

      &__title {
        @apply font-display text-black/90 uppercase text-3xl font-semibold;
        letter-spacing: 2.24px;
      }
      &__description {
        color: #555;
        font-size: 18px;
        line-height: 32px;
      }
    }
    &-video {
      @apply relative overflow-hidden select-none pointer-events-none;
      aspect-ratio: 1440 / 800;
      max-height: 800px;
      width: 100vw;
      margin: 0 calc(((var(--app-width) - var(--statu-container-width, var(--app-width)) + var(--statu-container-piece, 0px)) / 2 * -1) - 16px);

      video {
        @apply absolute inset-0 w-full h-full object-cover;
      }
    }
    &-info {
      @apply flex;

      &__media {
        @apply w-full flex-1 relative;

        :deep(img) {
          @apply absolute inset-0 w-full h-full object-cover;
        }

        &--left {
          @apply order-first;
        }
        &--right {
          @apply order-last;
        }
      }
      &__content {
        @apply flex-none flex flex-col text-center justify-center items-center gap-7 px-8 py-12 w-full;
        max-width: 470px;

        &-title {
          @apply font-display uppercase text-balance;
          color: rgba(0, 0, 0, 0.9);
          font-size: 24px;
          font-weight: 600;
          line-height: 36px;
          letter-spacing: 1.44px;
        }
        &-description {
          @apply text-pretty;
          color: #555;
          font-size: 18px;
          font-weight: 400;
          line-height: 32px;
        }
        &-link {
          color: #282828;
          font-size: 14px;
          font-weight: 700;
          line-height: 16px;
          letter-spacing: 3px;
          text-transform: uppercase;
          text-decoration: underline;
          text-underline-offset: 5px;
          padding-bottom: 5px;
          width: fit-content;
        }
      }
    }

    @include media("lg") {
      &-heading {
        @apply gap-3;

        &__title {
          @apply text-2xl;
          letter-spacing: 1.5px;
        }
        &__description {
          font-size: 16px;
          line-height: 28px;
        }
      }
      &-info {
        @apply flex-col gap-6;

        &__media {
          @apply aspect-video;

          &--left,
          &--right {
            @apply order-first;
          }
        }
        &__content {
          max-width: unset;
          @apply gap-3 p-0;

          &-title {
            @apply text-xl;
            letter-spacing: 1.5px;
          }
          &-description {
            font-size: 16px;
            line-height: 28px;
          }
          &-link {
            margin-top: 8px;
          }
        }
      }
    }
    @include media("md") {
      &-heading {
        @apply gap-1;

        &__title {
          @apply text-lg;
          letter-spacing: 1px;
        }
        &__description {
          @apply text-sm leading-6;
        }

        &:first-child {
          @apply pt-2;
        }
      }
      &-info {
        @apply gap-4;

        &__content {
          @apply gap-1;

          &-title {
            @apply text-lg leading-6;
            letter-spacing: 1px;
          }
          &-description {
            @apply text-sm leading-6;
          }
          &-link {
            margin-top: 8px;
            @apply text-xs;
          }
        }
      }
    }
  }
</style>
